import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty, isNil } from 'lodash';

import { navigate } from 'libs/router';
import PromptExit from 'libs/router/prompt-exit/PromptExit';

import PageSection from 'components/page/page-section/PageSection';
import { Form, FormActions } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';

import ProfileFormFieldsChannels from 'modules/influencers/components/profile/form/channels/ProfileFormFieldsChannels';

import {
  fetchInfluencerAudienceData,
  fetchInfluencerProfile,
  resetInfluencerProfile,
  saveInfluencerProfile
} from 'modules/influencers/reducers/influencerProfile';
import { danger } from 'redux-store/reducers/common-reducer';
import { validateChannels } from 'modules/channels/reducers/channels';

import { __t, applyTranslations } from 'utils/language';
import { getExpiredAnalyticsDataForInfluencer } from 'utils/influencers';

import './influencer-edit-profile-lightweight.scss';

import type {
  ChannelAnalyticsScreenShotData,
  ChannelForm,
  ChannelsValidationResult,
  InfluencerProfileForm
} from 'types';

applyTranslations(require('./influencer-edit-profile-lightweight.i18n.json'));

type OwnProps = {
  profileId: number,
  isAdmin: boolean
};

type StateProps = {
  profile: InfluencerProfileForm,
  hasConnectedFacebook: boolean
};

type DispatchProps = {
  dispatchFetchInfluencerProfile: (a: number) => Promise<void>,
  dispatchUpdateInfluencerProfile: (b: number, a: InfluencerProfileForm) => Promise<void>,
  dispatchResetInfluencerProfile: () => Promise<void>,
  dispatchValidateChannels: (a: Array<ChannelForm>) => Promise<ChannelsValidationResult>,
  dispatchDanger: (a: string) => Promise<void>,
  dispatchFetchInfluencerAudienceData: (a: number) => Promise<Array<ChannelAnalyticsScreenShotData>>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  details: {
    id: number | undefined | null,
    channels: Array<ChannelForm>,
    instagramExternalId?: null | string
  },
  isModified: boolean,
  hasError: boolean,
  expiredAnalyticsDataForInfluencer: {
    [x: string]: boolean
  }
};

class InfluencerEditProfileLightweight extends React.Component<Props, State> {
  state = {
    details: {
      id: null,
      channels: []
    },
    hasError: false,
    isModified: false,
    expiredAnalyticsDataForInfluencer: {}
  };

  componentDidMount() {
    const {
      profileId,
      hasConnectedFacebook,
      dispatchFetchInfluencerProfile,
      dispatchFetchInfluencerAudienceData
    } = this.props;

    const showTiktokExpiredAnalyticsOnly = hasConnectedFacebook;

    dispatchFetchInfluencerProfile(profileId);
    dispatchFetchInfluencerAudienceData(profileId)
      .then(data => {
        this.setState((prevState) => ({
          ...prevState,
          expiredAnalyticsDataForInfluencer: getExpiredAnalyticsDataForInfluencer(
            data,
            showTiktokExpiredAnalyticsOnly
          )
        }));
      });
  }

  componentDidUpdate(prevProps: Props) {
    const { profile } = this.props;

    if (isNil(profile) || isEmpty(profile)) {
      return;
    }

    if (profile.id === this.state.details.id) {
      return;
    }

    if (profile !== prevProps.profile) {
      this.setState(state => ({ // eslint-disable-line react/no-did-update-set-state
        details: {
          ...state.details,
          ...(profile || {})
        }
      }));
    }
  }

  componentWillUnmount() {
    const { dispatchResetInfluencerProfile } = this.props;

    dispatchResetInfluencerProfile();
  }

  submit = (validations: ChannelsValidationResult): void => {
    const { profileId, profile, dispatchUpdateInfluencerProfile } = this.props;
    const { details } = this.state;

    const updatedProfile = {
      ...profile,
      ...details,
      ...validations
    };

    dispatchUpdateInfluencerProfile(profileId, updatedProfile)
      .then((/* profile */) => {
        this.setState(() => ({
          isModified: false
        }), () => navigate.to(`/content-creator/profiles/${profileId}`));
      })
      .catch(() => {
        window.scrollTo(0, 0);
      });
  };

  handleDetailsChanged = (details: InfluencerProfileForm): void => {
    this.setState(state => ({
      isModified: true,
      // $FlowIgnore - cannot determine type for object literal
      details: {
        ...state.details,
        ...details
      }
    }));
  };

  handleChannelsChanged = (channels: Array<ChannelForm>): void => {
    this.setState(state => ({
      isModified: true,
      details: {
        ...state.details,
        channels
      }
    }));
  };

  handleSubmit = (): void => {
    const { dispatchValidateChannels } = this.props;
    const { details } = this.state;

    this.setState({
      hasError: false
    });

    dispatchValidateChannels(details.channels)
      .then((result: ChannelsValidationResult) => this.submit(result))
      .catch((result: ChannelsValidationResult) => {
        this.setState({
          details: {
            ...details,
            channels: result.channels,
            instagramExternalId: result.instagramExternalId
          }
        });

        this.handleValidationErrors();
      });
  };

  handleValidationErrors = (): void => {
    const { dispatchDanger } = this.props;

    this.setState({
      hasError: true
    });

    dispatchDanger(__t('influencer_edit_profile_lightweight.missing_channels'));

    window.scrollTo(0, 0);
  };

  render(): React.ReactNode {
    const { profileId, isAdmin } = this.props;
    const { details, isModified, expiredAnalyticsDataForInfluencer, hasError } = this.state;

    return (
      <div
        className="influencer-edit-profile-lightweight"
        data-qa="influencer-edit-profile-lightweight"
      >
        {
          isModified &&
            <PromptExit
              type="page"
            />
        }
        <PageSection
          title={__t('influencer_edit_profile_lightweight.heading')}
          titleProps={{ size: 'large' }}
        >
          <Form
            onSubmit={this.handleSubmit}
            onValidationErrors={this.handleValidationErrors}
          >
            <ProfileFormFieldsChannels
              channels={details.channels}
              isAdmin={isAdmin}
              hasError={hasError}
              expiredAnalyticsDataForInfluencer={expiredAnalyticsDataForInfluencer}
              onChange={this.handleChannelsChanged}
            />
            <FormActions align="center">
              <ActionButton
                color="tertiary"
                link={`/content-creator/profiles/${profileId}`}
              >
                {__t('influencer_edit_profile.buttons.button_cancel')}
              </ActionButton>
              <ActionButton
                color="primary"
                type="submit"
              >
                {__t('influencer_edit_profile.buttons.button_save')}
              </ActionButton>
            </FormActions>
          </Form>
        </PageSection>
      </div>
    );
  }
}

const mapStateToProps = ({ facebookButton, influencerProfile }): StateProps => ({
  profile: influencerProfile.profile,
  hasConnectedFacebook: facebookButton.isFBLoginActive && !facebookButton.isFBLoginExpired
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchInfluencerProfile: (profileId: number) => dispatch(fetchInfluencerProfile(profileId)),

  dispatchUpdateInfluencerProfile: (profileId: number, details: InfluencerProfileForm) => (
    dispatch(saveInfluencerProfile(profileId, details, true))
  ),

  dispatchResetInfluencerProfile: () => dispatch(resetInfluencerProfile()),
  dispatchDanger: (message: string) => dispatch(danger(message)),
  dispatchValidateChannels: (channels: Array<ChannelForm>) => dispatch(validateChannels(channels)),
  dispatchFetchInfluencerAudienceData: (profileId: number): Promise<Array<ChannelAnalyticsScreenShotData>> => dispatch(fetchInfluencerAudienceData(profileId))
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencerEditProfileLightweight);
